import { computed, unref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store.js';
import { escapeHtml } from 'shared/helpers/HTMLSanitizer';
import { useWhatsappGroup } from './useWhatsappGroup';

// WhatsApp writes a mention as the participant's internal id ("@190…"), which
// tells an agent nothing. The group's participant list turns it back into a
// name, or into the phone number when nobody in the account knows the person —
// the same fallback WhatsApp itself uses.
const MENTION_PATTERN = /@(\d{8,})/g;
const TAG_PATTERN = /(<[^>]*>)/;
const MENTION_CLASS = 'font-medium text-n-whatsapp-accent';

export const buildMentionNames = (participants = []) =>
  participants.reduce((names, participant) => {
    if (participant.lid) {
      names.set(participant.lid, participant.name || participant.phone);
    }
    return names;
  }, new Map());

// Ids that belong to nobody in the group are left alone: they are far more
// likely to be plain text than a mention.
const replaceMentions = (text, names, render) =>
  text.replace(MENTION_PATTERN, (mention, id) => {
    const name = names.get(id);

    return name ? render(name) : mention;
  });

// Runs over already rendered HTML, skipping tags so an id sitting inside an
// href is left alone.
export const highlightMentions = (html, names) => {
  if (!html?.includes('@') || !names.size) return html;

  return html
    .split(TAG_PATTERN)
    .map((chunk, index) =>
      index % 2
        ? chunk
        : replaceMentions(
            chunk,
            names,
            name => `<span class="${MENTION_CLASS}">@${escapeHtml(name)}</span>`
          )
    )
    .join('');
};

// The conversation list previews a message as plain text, so the id is simply
// swapped for the name.
export const resolveMentions = (text, participants) => {
  if (!text?.includes('@') || !participants?.length) return text;

  return replaceMentions(
    text,
    buildMentionNames(participants),
    name => `@${name}`
  );
};

// Names the people mentioned in a conversation card. The card already holds
// the contact, so the list spares itself a group lookup per conversation:
// it prefers a group the sidebar has just synced and otherwise reads what an
// earlier sync stored on the contact. The chat list keeps contacts as the API
// sends them while the contact sidebar camelises them, so both shapes are read.
export function usePreviewMentions(contact) {
  const syncedGroup = useWhatsappGroup(computed(() => unref(contact)?.id));

  const participants = computed(() => {
    const { additional_attributes: attributes, additionalAttributes } =
      unref(contact) || {};

    return (
      syncedGroup.value?.participants ??
      (attributes || additionalAttributes)?.group?.participants
    );
  });

  return text => resolveMentions(text, participants.value);
}

export function useGroupMentions() {
  const currentChat = useMapGetter('getSelectedChat');
  const group = useWhatsappGroup(
    computed(() => currentChat.value?.meta?.sender?.id)
  );
  const names = computed(() => buildMentionNames(group.value?.participants));

  return html => highlightMentions(html, names.value);
}
